import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import {
  PageContainer,
  PageHeader,
} from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebar,
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useBreadcrumbs } from "@/app-shell";
import { getErrorMessage } from "@/core/api/errors";
import useAuth from "@/core/auth/use-auth";
import { useBusiness } from "@/core/business";
import { usePayrollEmailsAccess } from "@/payroll/pay-breakdown/payroll-emails-access";
import { getPayBreakdownPath } from "@/payroll/pay-breakdown/routes";
import {
  PAYROLL_EMAILS_ACCESS,
  type PayrollEmailsAccess,
} from "@/payroll/pay-breakdown/types";
import { isTech } from "@/user/utils";
import { ChangedPacksBanner } from "./changed-packs-banner";
import { DeliveryPollingBanner } from "./delivery-polling-banner";
import { PacksFrame } from "./packs-frame";
import {
  PacksSectionProvider,
  PacksSidebar,
  usePacksSection,
} from "./packs-section";
import { ProjectionBuildBanner } from "./projection-build-banner";
import { ProjectionBuildLoadingState } from "./projection-build-loading-state";
import { RecipientsCard } from "./recipients-card";
import { SendConfirmationDialog } from "./send-confirmation-dialog";
import { SendFailuresBanner } from "./send-failures-banner";
import type { PayrollStatementNotificationChannel } from "./types";
import {
  getNotificationReasonLabel,
  getTextableRecipientsCount,
  getTextOnlyReach,
  isRenotifyOnly,
} from "./utils";

interface PayrollStatementNotificationsViewProps {
  businessId: string | null;
  periodStart: string | null;
  // When embedded in the "PayPacks" tab we drop the page chrome (the
  // "Back to Grid" button) and fill the tab's height instead of the full
  // page. The route page renders this with embedded=false.
  embedded?: boolean;
  payrollEmailsAccess: PayrollEmailsAccess;
  /**
   * Technicians with an open PackChat thread about this pack. They are marked and
   * sorted to the top, so a manager can see who is waiting without reading every
   * row. The questions themselves live on PackChat.
   */
  awaitingReplyTechnicianIds?: Set<string>;
}

// The payroll statement-emails feature, extracted from the route page so it can
// be mounted inline in the "PayPacks" tab as well as on its own route.
export const PayrollStatementNotificationsContent = ({
  embedded = false,
}: {
  // When embedded in the "PayPacks" tab we drop the page chrome (the
  // by the page from the same provider rather than inline here.
  embedded?: boolean;
}) => {
  // How the next send notifies technicians, for a business that texts PayPacks.
  // Kept across sends on the page, so a manager who texts only needn't untick
  // email every time.
  const [channels, setChannels] = useState<
    PayrollStatementNotificationChannel[]
  >(["email", "text"]);
  const {
    canUsePayrollEmails,
    data,
    error,
    projectionBuild,
    canRetryProjectionHydration,
    isProjectionRefreshing,
    refetchProjectionBuild,
    retryProjectionBuild,
    isRetryingProjection,
    previewOnly,
    recipients,
    eligibleRecipients,
    selectedEligibleRecipients,
    detailRecipient,
    closeDetail,
    periodLabel,
    sendDisabledReason,
    send,
    sendFailures,
    isSending,
    isSavingNote,
    deliveryPoll,
    missingReadyForEmailAccessKeys,
    isPayPacksLoading,
    canShowRecipients,
    showRecipientContent,
    confirmSendOpen,
    setConfirmSendOpen,
    periodStart,
    changedRecipients,
    blockedChangedRecipients,
    selectChangedPacks,
  } = usePacksSection();

  useBreadcrumbs(
    embedded
      ? null
      : [
          {
            label: "Payroll",
            href: getPayBreakdownPath(periodStart ?? undefined),
          },
          { label: "PayPacks" },
        ],
  );

  return (
    <SimpleSidebarLayout
      open={!embedded && !!detailRecipient}
      onOpenChange={(open) => {
        if (!open) closeDetail();
      }}
      sidebarWidth="420px"
      className={cn({ "h-full!": embedded })}
    >
      <SimpleSidebarContent removeScrollArea>
        <PacksFrame embedded={embedded}>
          {/* Standalone route chrome only: the page header (the trail goes
              back to the grid). When embedded in the "PayPacks" tab, the
              period header + active tab already say this, and the Send
              action lives in the recipients pane header below. */}
          {!embedded && (
            <PageHeader title="PayPacks" description={periodLabel} />
          )}

          {error && !data && (
            <Alert color="danger">
              <XCircleIcon />
              <AlertDescription>
                <Text variant="caption">Couldn't load this period.</Text>
                <Text tone="muted">{getErrorMessage(error)}</Text>
              </AlertDescription>
            </Alert>
          )}

          {canShowRecipients &&
            data &&
            !data.can_send &&
            canUsePayrollEmails && (
              <Alert color="warning" role="status">
                <WarningIcon />
                <AlertDescription>
                  <Text variant="caption">
                    These emails aren't ready to send yet.
                  </Text>
                  <Text tone="muted">
                    {getNotificationReasonLabel(data.cannot_send_reason) ??
                      "This period can't be published yet."}
                  </Text>
                </AlertDescription>
              </Alert>
            )}

          <ProjectionBuildBanner
            build={projectionBuild}
            canCheckAgain={canRetryProjectionHydration}
            isRefreshing={isProjectionRefreshing}
            isRetrying={isRetryingProjection}
            onCheckAgain={() => {
              void refetchProjectionBuild();
            }}
            onRetry={() => {
              retryProjectionBuild(undefined, {
                onError: (retryError) => {
                  toast.error("Couldn't retry the PayPack build", {
                    description: getErrorMessage(retryError),
                  });
                },
              });
            }}
          />

          {isPayPacksLoading && <ProjectionBuildLoadingState />}

          {showRecipientContent && (
            <>
              <DeliveryPollingBanner
                error={deliveryPoll.error}
                pollingTimedOut={!!deliveryPoll.data?.polling_timed_out}
                isRefreshing={deliveryPoll.isFetching}
                onCheckAgain={() => {
                  void deliveryPoll.refetch();
                }}
              />

              <SendFailuresBanner
                deliveries={sendFailures}
                recipients={recipients}
              />

              {/* Above the table rather than in the header: it is a finding about
                  this period, not a control, and it names the technicians it is
                  about. */}
              <ChangedPacksBanner
                changedRecipients={changedRecipients}
                blockedRecipients={blockedChangedRecipients}
                onSelect={selectChangedPacks}
                disabled={previewOnly}
              />

              <RecipientsCard embedded={embedded} />
            </>
          )}
        </PacksFrame>
      </SimpleSidebarContent>

      <SimpleSidebar>{!embedded && <PacksSidebar />}</SimpleSidebar>

      {canUsePayrollEmails && (
        <SendConfirmationDialog
          open={confirmSendOpen}
          isSending={isSending}
          isSavingNote={isSavingNote}
          sendDisabledReason={sendDisabledReason}
          periodLabel={periodLabel}
          selectedEligibleCount={selectedEligibleRecipients.length}
          renotify={isRenotifyOnly(selectedEligibleRecipients)}
          eligibleCount={eligibleRecipients.length}
          textCount={
            data?.sms_enabled
              ? getTextableRecipientsCount(selectedEligibleRecipients)
              : null
          }
          {...getTextOnlyReach(selectedEligibleRecipients)}
          channels={data?.sms_enabled ? channels : null}
          onChannelsChange={setChannels}
          missingReadyForEmailAccessKeys={missingReadyForEmailAccessKeys}
          onOpenChange={setConfirmSendOpen}
          onSend={async () => {
            if (await send(data?.sms_enabled ? channels : null))
              setConfirmSendOpen(false);
          }}
        />
      )}
    </SimpleSidebarLayout>
  );
};

export const PayrollStatementNotificationsView = ({
  embedded,
  ...input
}: PayrollStatementNotificationsViewProps) => (
  <PacksSectionProvider
    key={`${input.businessId ?? ""}:${input.periodStart ?? ""}`}
    {...input}
  >
    <PayrollStatementNotificationsContent embedded={embedded} />
  </PacksSectionProvider>
);

// Route page: resolves business + period from context/params, keeps the
// technician redirect, and renders the shared view with full page chrome.
const PayrollStatementNotificationsPage = () => {
  const { periodStart } = useParams<{ periodStart: string }>();
  const { businessId } = useBusiness();
  const { user } = useAuth();
  const { payrollEmailsAccess, isLoading } = usePayrollEmailsAccess();

  if (isTech(user?.role)) {
    return <Navigate to={getPayBreakdownPath(periodStart)} replace />;
  }

  if (isLoading)
    return (
      <PageContainer variant="workspace">
        <PageHeader title="PayPacks" />
        <Spinner layout="centered" />
      </PageContainer>
    );

  if (payrollEmailsAccess === PAYROLL_EMAILS_ACCESS.hidden) {
    return <Navigate to={getPayBreakdownPath(periodStart)} replace />;
  }

  return (
    <PayrollStatementNotificationsView
      businessId={businessId}
      periodStart={periodStart ?? null}
      payrollEmailsAccess={payrollEmailsAccess}
    />
  );
};

export default PayrollStatementNotificationsPage;
